import React from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import GreyMenu from './GreyMenu';
import NewLead from '../leads/NewLead';
import { useDispatch, useSelector } from 'react-redux';
import { toggleGreyMenu, setActiveMenuItem, setActiveSubmenuItem, closeGreyMenu } from '../../store/slices/menuSlice';
import { logout } from '../../store/slices/authSlice';
import { fetchMenuColor, setMenuLayout } from '../../store/slices/usersSlice';
import WideDialog from '../common/WideDialog';
import { useState } from 'react';
import UsersTab from '../settings/UsersTab';
import CostGroupsTab from '../settings/CostGroupsTab';
import CostCodesTab from '../settings/CostCodesTab';
import AppSettingsTab from '../settings/AppSettingsTab';
import CategoriesTab from '../settings/CategoriesTab';
import CitiesTab from '../settings/CitiesTab';
import AppearanceTab from '../settings/AppearanceTab';
import BackupsTab from '../settings/BackupsTab';

const ClassicMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeMenuItem } = useSelector(state => state.menu);
  const { menuColor, customMenuColor } = useSelector(state => state.users);
  const [isNewLeadOpen, setIsNewLeadOpen] = React.useState(false);
  const location = useLocation();
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [isMaximized, setIsMaximized] = useState(true);
  const { user } = useSelector(state => state.auth);
  const isAdmin = user && (user.profile.role === 'admin' || user.profile.role === 'super_admin');
  const [isInitialized, setIsInitialized] = React.useState(false);

  const settingsTabs = [
    { id: 'users', name: 'Users' },
    { id: 'categories', name: 'Categories' },
    { id: 'costGroups', name: 'Cost Groups' },
    { id: 'costCodes', name: 'Cost Codes' },
    { id: 'cities', name: 'Cities' },
    { id: 'appSettings', name: 'App Settings' },
    { id: 'appearance', name: 'Appearance' },
    ...(isAdmin ? [{ id: 'backups', name: 'Backups' }] : [])
  ];

  // Fetch menu color on component mount
  React.useEffect(() => {
    dispatch(fetchMenuColor());
  }, [dispatch]);

  // Initial loading effect
  React.useEffect(() => {
    // Only initialize once and only if we have a stored activeMenuItem
    if (!isInitialized && activeMenuItem) {
      // Don't navigate automatically - just mark as initialized
      // This prevents the flash when accessing grey menu items
      setIsInitialized(true);
    }
  }, [activeMenuItem, isInitialized]);

  // Update useEffect to handle maintaining menu state across refreshes
  React.useEffect(() => {
    const path = location.pathname;

    // Only set activeMenuItem if it's a new path (different from what's in redux)
    if (path !== activeMenuItem) {
      dispatch(setActiveMenuItem(path));
    }

    // Set the correct grey menu state based on active path
    const setupMenuState = (currentPath) => {
      if (currentPath === '/projects/calendar' ||
        currentPath === '/weather' ||
        currentPath === '/safety' ||
        currentPath === '/clients' ||
        currentPath === '/inquiries' ||
        currentPath === '/ai' ||
        currentPath === '/roof-measurement') {
        dispatch(closeGreyMenu());
      } else if (currentPath === '/') {
        dispatch(toggleGreyMenu('leads'));
        dispatch(setActiveSubmenuItem('/leads'));
      } else if (currentPath.startsWith('/leads')) {
        dispatch(toggleGreyMenu('leads'));
        dispatch(setActiveSubmenuItem(currentPath));
      } else if (currentPath.startsWith('/projects')) {
        dispatch(toggleGreyMenu('projects'));
        dispatch(setActiveSubmenuItem(currentPath));
      } else {
        dispatch(closeGreyMenu());
      }
    };

    // Only set up menu state for the current path to prevent flashing
    setupMenuState(path);
  }, [location.pathname, dispatch, activeMenuItem]);

  // Remove local state management since it's now in Redux
  const isActive = (path) => activeMenuItem === path;

  // Update handleMenuClick to handle calendar specifically
  const handleMenuClick = (path, section = null) => {
    dispatch(setActiveMenuItem(path));
    if (path === '/' || path === '/projects/calendar' || path === '/weather' || path === '/safety' || path === '/clients' || path === '/inquiries' || path === '/ai' || path === '/roof-measurement') {
      // Ensure grey menu is hidden for dashboard, calendar, weather, safety, clients and roof measurement
      dispatch(toggleGreyMenu(null));
      dispatch(closeGreyMenu());
      dispatch(setActiveSubmenuItem(null));
    } else if (section) {
      dispatch(toggleGreyMenu(section));
      const defaultSubmenuPath = section === 'leads' ? '/leads' : '/projects';
      dispatch(setActiveSubmenuItem(defaultSubmenuPath));
    } else {
      dispatch(toggleGreyMenu(null));
      dispatch(setActiveSubmenuItem(null));
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Toggle maximize/minimize for settings dialog
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Helper function to generate colors from hex
  const generateColorsFromHex = (hexColor) => {
    // Convert hex to RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Generate darker and lighter variations
    const darken = (amount) => {
      return `rgb(${Math.max(0, r - amount)}, ${Math.max(0, g - amount)}, ${Math.max(0, b - amount)})`;
    };

    const lighten = (amount) => {
      return `rgb(${Math.min(255, r + amount)}, ${Math.min(255, g + amount)}, ${Math.min(255, b + amount)})`;
    };

    return {
      sidebar: '',
      sidebarColor: hexColor,
      hover: `hover:bg-[${darken(20)}]`,
      active: `bg-[${darken(40)}]`,
      inactive: `text-[${lighten(120)}]`,
      tooltip: `bg-[${darken(40)}]`,
      mobile: '',
      mobileColor: hexColor,
      mobileInactive: `text-[${lighten(100)}]`
    };
  };

  // Get dynamic color classes based on user preference
  const getColorClasses = (color = 'blue', customHex = null) => {
    // Handle custom colors
    if (color === 'custom' && customHex) {
      return generateColorsFromHex(customHex);
    }

    const colorMap = {
      blue: {
        sidebar: 'bg-blue-700',
        sidebarColor: '#1d4ed8', // blue-700
        hover: 'hover:bg-blue-800',
        active: 'bg-blue-900',
        inactive: 'text-blue-100',
        tooltip: 'bg-blue-900',
        mobile: 'bg-blue-700 border-blue-600',
        mobileColor: '#1d4ed8',
        mobileInactive: 'text-blue-200'
      },
      indigo: {
        sidebar: 'bg-indigo-700',
        sidebarColor: '#4338ca', // indigo-700
        hover: 'hover:bg-indigo-800',
        active: 'bg-indigo-900',
        inactive: 'text-indigo-100',
        tooltip: 'bg-indigo-900',
        mobile: 'bg-indigo-700 border-indigo-600',
        mobileColor: '#4338ca',
        mobileInactive: 'text-indigo-200'
      },
      purple: {
        sidebar: 'bg-purple-700',
        sidebarColor: '#7c3aed', // purple-700
        hover: 'hover:bg-purple-800',
        active: 'bg-purple-900',
        inactive: 'text-purple-100',
        tooltip: 'bg-purple-900',
        mobile: 'bg-purple-700 border-purple-600',
        mobileColor: '#7c3aed',
        mobileInactive: 'text-purple-200'
      },
      green: {
        sidebar: 'bg-green-700',
        sidebarColor: '#15803d', // green-700
        hover: 'hover:bg-green-800',
        active: 'bg-green-900',
        inactive: 'text-green-100',
        tooltip: 'bg-green-900',
        mobile: 'bg-green-700 border-green-600',
        mobileColor: '#15803d',
        mobileInactive: 'text-green-200'
      },
      red: {
        sidebar: 'bg-red-700',
        sidebarColor: '#b91c1c', // red-700
        hover: 'hover:bg-red-800',
        active: 'bg-red-900',
        inactive: 'text-red-100',
        tooltip: 'bg-red-900',
        mobile: 'bg-red-700 border-red-600',
        mobileColor: '#b91c1c',
        mobileInactive: 'text-red-200'
      },
      orange: {
        sidebar: 'bg-orange-700',
        sidebarColor: '#c2410c', // orange-700
        hover: 'hover:bg-orange-800',
        active: 'bg-orange-900',
        inactive: 'text-orange-100',
        tooltip: 'bg-orange-900',
        mobile: 'bg-orange-700 border-orange-600',
        mobileColor: '#c2410c',
        mobileInactive: 'text-orange-200'
      },
      teal: {
        sidebar: 'bg-teal-700',
        sidebarColor: '#0f766e', // teal-700
        hover: 'hover:bg-teal-800',
        active: 'bg-teal-900',
        inactive: 'text-teal-100',
        tooltip: 'bg-teal-900',
        mobile: 'bg-teal-700 border-teal-600',
        mobileColor: '#0f766e',
        mobileInactive: 'text-teal-200'
      },
      gray: {
        sidebar: 'bg-gray-700',
        sidebarColor: '#374151', // gray-700
        hover: 'hover:bg-gray-800',
        active: 'bg-gray-900',
        inactive: 'text-gray-100',
        tooltip: 'bg-gray-900',
        mobile: 'bg-gray-700 border-gray-600',
        mobileColor: '#374151',
        mobileInactive: 'text-gray-200'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const colors = getColorClasses(menuColor, customMenuColor);

  // Updated base classes for menu items with dynamic colors
  const baseIconClasses = `group relative flex items-center justify-center h-12 w-12 rounded-lg transition-colors duration-200 ${colors.hover}`;
  const activeIconClasses = `${colors.active} text-white`;
  const inactiveIconClasses = colors.inactive;

  // Updated tooltip classes with dynamic colors
  const tooltipClasses = `sidebar-tooltip absolute left-14 rounded-md px-2 py-1 ml-2 ${colors.tooltip} text-white text-sm invisible opacity-0 -translate-x-3 group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-100 z-50`;

  const tabs = [
    { id: 'users', name: 'Users' },
    { id: 'categories', name: 'Categories' },
    { id: 'costGroups', name: 'Cost Groups' },
    { id: 'costCodes', name: 'Cost Codes' },
    { id: 'cities', name: 'Cities' },
    { id: 'appearance', name: 'Appearance' },
  ];

  return (
    <>
      {/* Desktop Sidebar - Updated with fixed width and z-index */}
      <aside
        className="sidebar fixed left-0 w-16 md:w-[64px] lg:w-[72px] xl:w-[80px] h-screen hidden md:flex flex-col items-center z-20"
        style={{ backgroundColor: getColorClasses(menuColor, customMenuColor).sidebarColor }}
      >
        <div className="flex flex-col items-center gap-6 pt-4 mt-12">
          {/* Dashboard - Only shown to admins and super_admins */}
          {isAdmin && (
            <Link
              to="/"
              className={`${baseIconClasses} ${isActive('/') ? activeIconClasses : inactiveIconClasses}`}
              onClick={() => handleMenuClick('/')}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="9" rx="1.5" />
                <rect x="14" y="3" width="7" height="5" rx="1.5" />
                <rect x="14" y="12" width="7" height="9" rx="1.5" />
                <rect x="3" y="16" width="7" height="5" rx="1.5" />
              </svg>
              <span className={tooltipClasses}>Dashboard</span>
            </Link>
          )}

          {/* Leads & Estimates */}
          <Link
            to="/leads"
            className={`${baseIconClasses} ${isActive('/leads') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/leads', 'leads')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="8" r="3.5" />
              <path d="M2.5 20a6.5 6.5 0 0113 0" />
              <path d="M18 6v6M15 9h6" />
            </svg>
            <span className={tooltipClasses}>Leads & Estimates</span>
          </Link>


          {/* Project Management - Updated */}
          <Link
            to="/projects"
            className={`${baseIconClasses} ${isActive('/projects') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/projects', 'projects')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="7" width="18" height="13" rx="2" />
              <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
              <path d="M3 13h18" />
            </svg>
            <span className={tooltipClasses}>Project Management</span>
          </Link>

          {/* Clients */}
          <Link
            to="/clients"
            className={`${baseIconClasses} ${isActive('/clients') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/clients')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="8" r="3.5" />
              <path d="M2.5 20a6.5 6.5 0 0113 0" />
              <circle cx="17" cy="9" r="2.5" />
              <path d="M15 20a4.5 4.5 0 016.5-4" />
            </svg>
            <span className={tooltipClasses}>Clients</span>
          </Link>

          {/* Website Inquiries — hidden
          <Link
            to="/inquiries"
            className={`${baseIconClasses} ${isActive('/inquiries') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/inquiries')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a8 8 0 01-11.6 7.16L4 20l.84-5.4A8 8 0 1121 12z" />
              <circle cx="9" cy="12" r="0.8" fill="currentColor" stroke="none" />
              <circle cx="12.5" cy="12" r="0.8" fill="currentColor" stroke="none" />
              <circle cx="16" cy="12" r="0.8" fill="currentColor" stroke="none" />
            </svg>
            <span className={tooltipClasses}>Website Inquiries</span>
          </Link>
          */}


          {/* HR/Time Management - Now disabled */}
          {/* <div 
            className={`${baseIconClasses} opacity-50 cursor-not-allowed`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 6v6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span className={tooltipClasses}>HR/Time Management (Coming Soon)</span>
          </div> */}

          {/* Safety - Now functional instead of disabled */}
          <Link
            to="/safety"
            className={`${baseIconClasses} ${isActive('/safety') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/safety')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l8 3v6c0 4.5-3.4 8.4-8 9.5-4.6-1.1-8-5-8-9.5V6l8-3z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <span className={tooltipClasses}>File Storage</span>
          </Link>

          {/* Weather & Map */}
          <Link
            to="/weather"
            className={`${baseIconClasses} ${isActive('/weather') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/weather')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="7" cy="8" r="3" />
              <path d="M7 2v1M7 13v1M2 8h1M11 8h1M3.5 4.5l.7.7M9.8 10.8l.7.7M3.5 11.5l.7-.7M9.8 5.2l.7-.7" />
              <path d="M14 18h5a3 3 0 100-6 4.5 4.5 0 00-8.6.5A3.5 3.5 0 1011 18h3z" />
            </svg>
            <span className={tooltipClasses}>Weather & Map</span>
          </Link>

          {/* Roof Measurement */}
          <Link
            to="/roof-measurement"
            className={`${baseIconClasses} ${isActive('/roof-measurement') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/roof-measurement')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12L12 3l9 9" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-5h6v5" />
            </svg>
            <span className={tooltipClasses}>Roof Measurement</span>
          </Link>

          {/* Calendar - Updated to be a Link */}
          <Link
            to="/projects/calendar"
            className={`${baseIconClasses} ${isActive('/projects/calendar') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/projects/calendar')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 10h18" />
              <path d="M8 3v4M16 3v4" />
              <circle cx="8" cy="15" r="1" fill="currentColor" stroke="none" />
              <circle cx="12" cy="15" r="1" fill="currentColor" stroke="none" />
              <circle cx="16" cy="15" r="1" fill="currentColor" stroke="none" />
            </svg>
            <span className={tooltipClasses}>Calendar</span>
          </Link>

          {/* AI Chat */}
          <Link
            to="/ai"
            className={`${baseIconClasses} ${isActive('/ai') ? activeIconClasses : inactiveIconClasses}`}
            onClick={() => handleMenuClick('/ai')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 3l1.8 4.7L17.5 9l-4.7 1.8L11 15l-1.8-4.2L4.5 9l4.7-1.3L11 3z" />
              <path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9L18 14z" />
            </svg>
            <span className={tooltipClasses}>Topsail GPT</span>
          </Link>
        </div>

        {/* Bottom icons */}
        <div className="flex flex-col items-center gap-8 mt-auto mb-8">
          {/* Color Theme */}
          <button
            onClick={() => {
              setIsSettingsOpen(true);
              setActiveTab('appearance');
            }}
            className={`${baseIconClasses} ${inactiveIconClasses}`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3a9 9 0 100 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.4-.3-.4-.5-.9-.5-1.4 0-1.1.9-2 2-2h2a4 4 0 004-4 8 8 0 00-9-7.2z" />
              <circle cx="7.5" cy="11" r="1.2" fill="currentColor" stroke="none" />
              <circle cx="10.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
              <circle cx="15" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
              <circle cx="17.5" cy="11" r="1.2" fill="currentColor" stroke="none" />
            </svg>
            <span className={tooltipClasses}>Color Theme</span>
          </button>

          {/* Settings */}
          <button
            onClick={() => setIsSettingsOpen(true)}
            className={`${baseIconClasses} ${inactiveIconClasses}`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span className={tooltipClasses}>Settings</span>
          </button>

          {/* Switch to Modern Layout */}
          <button
            onClick={() => dispatch(setMenuLayout('modern'))}
            className={`${baseIconClasses} ${inactiveIconClasses}`}
            title="Switch to Modern Layout"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="18" rx="2" />
              <rect x="13" y="3" width="8" height="9" rx="2" />
              <rect x="13" y="15" width="8" height="6" rx="2" />
            </svg>
            <span className={tooltipClasses}>Modern Layout</span>
          </button>

          {/* Sign out */}
          <button
            onClick={handleLogout}
            className={`${baseIconClasses} ${inactiveIconClasses}`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            <span className={tooltipClasses}>Sign Out</span>
          </button>

          {/* Refresh */}
          {/* <div className="sidebar-icon">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div> */}
        </div>
      </aside>

      {/* Mobile Bottom Navigation - Updated z-index */}
      <nav
        className="fixed bottom-0 left-0 right-0 md:hidden z-30 border-t"
        style={{
          backgroundColor: getColorClasses(menuColor, customMenuColor).mobileColor,
          borderTopColor: `${getColorClasses(menuColor, customMenuColor).mobileColor}cc` // slightly transparent
        }}
      >
        <div className="flex justify-around items-center h-16">
          {/* Dashboard - Mobile - Only for admins */}
          {isAdmin && (
            <Link
              to="/"
              className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/') ? 'text-white' : colors.mobileInactive}`}
              onClick={() => handleMenuClick('/')}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline points="9,22 9,12 15,12 15,22"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xs mt-1">Dashboard</span>
            </Link>
          )}

          {/* Leads - Updated active/inactive colors */}
          <Link
            to="/leads"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/leads') ? 'text-white' : colors.mobileInactive}`}
            onClick={() => handleMenuClick('/leads', 'leads')}
          >
            <div className="relative">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round" />
              </svg>
              <div className="absolute -right-1 -top-1 w-2.5 h-2.5 bg-orange-500 rounded-full"></div>
            </div>
            <span className="text-xs mt-1">Leads</span>
          </Link>

          {/* Clients - Mobile */}
          <Link
            to="/clients"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/clients') ? 'text-white' : colors.mobileInactive}`}
            onClick={() => handleMenuClick('/clients')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-xs mt-1">Clients</span>
          </Link>

          {/* Projects - Updated active/inactive colors */}
          <Link
            to="/projects"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/projects') ? 'text-white' : colors.mobileInactive}`}
            onClick={() => handleMenuClick('/projects', 'projects')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3-4h3m-6 4h1m-1 4h1m5-4h1m-1 4h1" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <span className="text-xs mt-1">Projects</span>
          </Link>

          {/* Calendar - Mobile */}
          <Link
            to="/projects/calendar"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/projects/calendar') ? 'text-white' : colors.mobileInactive}`}
            onClick={() => handleMenuClick('/projects/calendar')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span className="text-xs mt-1">Calendar</span>
          </Link>

          {/* Weather - Updated active/inactive colors */}
          <Link
            to="/weather"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/weather') ? 'text-white' : colors.mobileInactive}`}
            onClick={() => handleMenuClick('/weather')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M3 15a4 4 0 004 4h9a5 5 0 001.7-9.7 7 7 0 00-13.4 1.9A4 4 0 003 15z" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <span className="text-xs mt-1">Weather</span>
          </Link>

          {/* Roof Measurement - Mobile */}
          <Link
            to="/roof-measurement"
            className={`flex flex-col items-center justify-center flex-1 py-2 ${isActive('/roof-measurement') ? 'text-white' : colors.mobileInactive}`}
            onClick={() => handleMenuClick('/roof-measurement')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
              <path d="M3 12L12 4L21 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M5 10V20H19V10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M8 15H16M8 15L10 13M8 15L10 17M16 15L14 13M16 15L14 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-xs mt-1">Measure</span>
          </Link>
        </div>
      </nav>

      {/* Add margin to push content away from fixed sidebar */}
      <div className="hidden md:block w-16 md:w-[64px] lg:w-[72px] xl:w-[80px] flex-shrink-0">
      </div>

      {/* Render GreyMenu conditionally */}
      {location.pathname !== '/projects/calendar' &&
        location.pathname !== '/weather' &&
        location.pathname !== '/safety' &&
        location.pathname !== '/clients' &&
        location.pathname !== '/inquiries' &&
        location.pathname !== '/roof-measurement' &&
        <GreyMenu onNewLead={() => setIsNewLeadOpen(true)} />
      }

      <NewLead
        isOpen={isNewLeadOpen}
        onClose={() => setIsNewLeadOpen(false)}
      />

      {/* Settings Dialog - Updated with maximize/minimize functionality */}
      <WideDialog isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}>
        <div className={`${isMaximized ? 'fixed inset-0 z-50 bg-white' : 'bg-white rounded-lg shadow-xl max-w-7xl mx-auto'}`}>
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800">Settings</h2>
            <div className="flex items-center space-x-2">
              {/* Maximize/restore icon */}
              <button
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                onClick={toggleMaximize}
                title={isMaximized ? "Restore dialog size" : "Maximize dialog"}
              >
                {isMaximized ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>

              {/* Close icon */}
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {settingsTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6 overflow-y-auto" style={{ height: isMaximized ? 'calc(100vh - 130px)' : 'min(70vh, 640px)' }}>
            {activeTab === 'users' && <UsersTab isMaximized={isMaximized} />}
            {activeTab === 'categories' && <CategoriesTab />}
            {activeTab === 'costGroups' && <CostGroupsTab />}
            {activeTab === 'costCodes' && <CostCodesTab />}
            {activeTab === 'cities' && <CitiesTab />}
            {activeTab === 'appSettings' && <AppSettingsTab />}
            {activeTab === 'appearance' && <AppearanceTab />}
            {activeTab === 'backups' && <BackupsTab />}
          </div>
        </div>
      </WideDialog>
    </>
  );
};

export default ClassicMenu;
